<template>
	<div>
		<dl class="row row-cols-1 row-cols-md-2 g-2 mb-0 small">
			<div class="col">
				<dt class="text-body-secondary fw-normal">Kind</dt>
				<dd class="mb-0">
					<span
						class="badge rounded-pill"
						:class="kindBadgeClass(assignment.kind)"
					>
						{{ kindLabel(assignment.kind) }}
					</span>
				</dd>
			</div>
			<div class="col">
				<dt class="text-body-secondary fw-normal">State</dt>
				<dd class="mb-0">
					<span
						class="badge rounded-pill"
						:class="stateBadgeClass(assignment.state)"
					>
						{{ stateLabel(assignment.state) }}
					</span>
				</dd>
			</div>
			<div class="col">
				<dt class="text-body-secondary fw-normal">Technician</dt>
				<dd class="mb-0">
					{{ technicianLabel(assignment.technicianId, users) }}
				</dd>
			</div>
			<div class="col">
				<dt class="text-body-secondary fw-normal">Window</dt>
				<dd class="mb-0">{{ assignmentWindowText(assignment) }}</dd>
			</div>
			<div class="col">
				<dt class="text-body-secondary fw-normal">Target</dt>
				<dd class="mb-0">{{ targetLabel(assignment, sites) }}</dd>
			</div>
			<div class="col">
				<dt class="text-body-secondary fw-normal">Route</dt>
				<dd class="mb-0">
					<template v-if="assignment.routeId !== null">
						<button
							type="button"
							class="btn btn-link btn-sm p-0 align-baseline"
							@click="emit('select', { kind: 'route', id: assignment.routeId })"
						>
							Route #{{ assignment.routeId }}
						</button>
						<span v-if="assignment.routePosition !== null">
							at slot {{ assignment.routePosition + 1 }}
						</span>
						<span v-else class="text-body-secondary">(unsequenced)</span>
					</template>
					<span v-else class="text-body-secondary">Not on a route</span>
				</dd>
			</div>
			<div v-if="configurationName" class="col">
				<dt class="text-body-secondary fw-normal">Configuration</dt>
				<dd class="mb-0">{{ configurationName }}</dd>
			</div>
			<div class="col">
				<dt class="text-body-secondary fw-normal">Created</dt>
				<dd class="mb-0">{{ formatDate(new Date(assignment.createdAt)) }}</dd>
			</div>
			<div v-if="assignment.completedAt" class="col">
				<dt class="text-body-secondary fw-normal">Completed</dt>
				<dd class="mb-0">{{ formatDate(new Date(assignment.completedAt)) }}</dd>
			</div>
			<div v-if="assignment.instructions" class="col-12">
				<dt class="text-body-secondary fw-normal">Instructions</dt>
				<dd class="mb-0" style="white-space: pre-wrap">
					{{ assignment.instructions }}
				</dd>
			</div>
			<div v-if="assignment.stateReason" class="col-12">
				<dt class="text-body-secondary fw-normal">Reason recorded</dt>
				<dd class="mb-0">{{ assignment.stateReason }}</dd>
			</div>
		</dl>

		<div class="d-flex flex-wrap gap-2 mt-3">
			<button
				v-for="target in targets"
				:key="target"
				type="button"
				class="btn btn-sm btn-outline-primary"
				:disabled="busy"
				@click="choose(target)"
			>
				Move to {{ stateLabel(target) }}
			</button>
		</div>

		<!-- §7 demands a recorded reason for deferred and cancelled; the field
		     appears only once one of those is chosen. -->
		<div v-if="pendingTarget" class="mt-2">
			<label class="form-label small fw-semibold" for="assignmentReason">
				Why is this moving to {{ stateLabel(pendingTarget) }}?
			</label>
			<textarea
				id="assignmentReason"
				v-model="reason"
				class="form-control form-control-sm"
				rows="2"
				placeholder="The reason is recorded with the state."
			></textarea>
			<div class="d-flex gap-2 mt-2">
				<button
					type="button"
					class="btn btn-sm btn-primary"
					:disabled="busy || reason.trim() === ''"
					@click="confirmReason()"
				>
					Move to {{ stateLabel(pendingTarget) }}
				</button>
				<button
					type="button"
					class="btn btn-sm btn-outline-secondary"
					:disabled="busy"
					@click="cancelReason"
				>
					Cancel
				</button>
			</div>
		</div>

		<p v-if="targets.length === 0" class="text-body-secondary small mb-0 mt-3">
			{{
				assignment.state === "completed" || assignment.state === "cancelled"
					? "This assignment is settled; §7 offers no edge out of it."
					: "§7 offers no move from this state."
			}}
		</p>

		<!-- The Route a draft Assignment joins. The door binds a Route to one
		     technician, so only that technician's editable routes are offered. -->
		<div
			v-if="assignment.routeId === null && editableRoutes.length > 0"
			class="mt-3"
		>
			<label class="form-label small fw-semibold" for="assignmentRoute">
				Add to a route
			</label>
			<div class="input-group input-group-sm">
				<select
					id="assignmentRoute"
					v-model.number="addRouteId"
					class="form-select"
					:disabled="busy"
				>
					<option :value="0" disabled>Choose a route…</option>
					<option
						v-for="route in editableRoutes"
						:key="route.id"
						:value="route.id"
					>
						#{{ route.id }} — {{ routeWindowText(route) }}
					</option>
				</select>
				<button
					type="button"
					class="btn btn-outline-primary"
					:disabled="busy || addRouteId <= 0"
					@click="addToRoute()"
				>
					Add
				</button>
			</div>
		</div>

		<!-- A refused move keeps the server's words: a completion refusal names
		     the evidence that is missing, and rewording it would hide the one
		     instruction the operator needs. -->
		<div v-if="transitionError" class="alert alert-danger py-2 small mt-3 mb-0">
			{{ transitionError }}
		</div>
		<div v-else-if="routeError" class="alert alert-danger py-2 small mt-3 mb-0">
			{{ routeError }}
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

import { apiClient } from "@/client";
import { formatDate } from "@/format";
import type {
	AssignmentState,
	AssignmentStateUpdate,
	MapReviewSiteRow,
	SyncAssignment,
	SyncRoute,
	TrapConfigurationResult,
	User,
} from "@/type/api";

import type { Selection } from "./model";
import {
	assignmentWindowText,
	describeError,
	kindBadgeClass,
	kindLabel,
	routeWindowText,
	stateBadgeClass,
	stateLabel,
	targetLabel,
	technicianLabel,
	transitionTargets,
} from "./wire";

const props = defineProps<{
	assignment: SyncAssignment;
	users: User[];
	sites: MapReviewSiteRow[];
	trapConfigurations: TrapConfigurationResult[];
	routes: SyncRoute[];
}>();

const emit = defineEmits<{
	notify: [title: string, message: string];
	changed: [];
	select: [selection: Selection];
}>();

const pendingTarget = ref<AssignmentState | null>(null);
const reason = ref("");
const busy = ref(false);
const transitionError = ref("");
const routeError = ref("");
const addRouteId = ref(0);

const targets = computed(() => transitionTargets(props.assignment.state));

const configurationName = computed(() => {
	if (props.assignment.trapConfigurationId === null) return "";
	const configuration = props.trapConfigurations.find(
		(candidate) => candidate.id === props.assignment.trapConfigurationId,
	);
	return configuration
		? `${configuration.name} (v${configuration.version})`
		: `Configuration #${props.assignment.trapConfigurationId}`;
});

const editableRoutes = computed(() =>
	props.routes.filter(
		(route) =>
			route.technicianId === props.assignment.technicianId &&
			route.state !== "completed" &&
			route.state !== "cancelled",
	),
);

// A reload may move the assignment to another state (or another Route); the
// pending reason belongs to the row that was on screen when it was typed.
watch(
	() => props.assignment.id,
	() => {
		pendingTarget.value = null;
		reason.value = "";
		transitionError.value = "";
		routeError.value = "";
		addRouteId.value = 0;
	},
);

function choose(target: AssignmentState) {
	transitionError.value = "";
	if (target === "deferred" || target === "cancelled") {
		pendingTarget.value = target;
		reason.value = "";
		return;
	}
	void move(target, null);
}

function cancelReason() {
	pendingTarget.value = null;
	reason.value = "";
}

async function confirmReason() {
	if (!pendingTarget.value) return;
	const text = reason.value.trim();
	if (text === "") return;
	await move(pendingTarget.value, text);
}

// The one state door. A refusal is shown as the server stated it — a completion
// refusal names the record that is missing — and the page re-reads only after a
// move that succeeded, so a refused edge leaves the row exactly as it was.
async function move(to: AssignmentState, reasonText: string | null) {
	busy.value = true;
	transitionError.value = "";
	try {
		const body: AssignmentStateUpdate = { to, reason: reasonText };
		const updated = await apiClient.JSONPut<SyncAssignment>(
			`/api/assignment/${props.assignment.id}/state`,
			body,
		);
		pendingTarget.value = null;
		reason.value = "";
		emit(
			"notify",
			`Assignment #${updated.id} ${stateLabel(updated.state).toLowerCase()}`,
			updated.completedAt
				? `Completed at ${formatDate(new Date(updated.completedAt))}.`
				: `Moved from ${stateLabel(props.assignment.state).toLowerCase()}.`,
		);
		emit("changed");
	} catch (e: unknown) {
		transitionError.value = describeError(e);
		emit("notify", "Assignment state change refused", transitionError.value);
	} finally {
		busy.value = false;
	}
}

async function addToRoute() {
	if (addRouteId.value <= 0) return;
	busy.value = true;
	routeError.value = "";
	try {
		await apiClient.JSONPut(`/api/route/${addRouteId.value}/assignment`, {
			assignment_id: props.assignment.id,
			position: null,
		});
		emit(
			"notify",
			"Added to route",
			`Assignment #${props.assignment.id} joined route #${addRouteId.value} unsequenced.`,
		);
		addRouteId.value = 0;
		emit("changed");
	} catch (e: unknown) {
		routeError.value = describeError(e);
		emit("notify", "Assignment not added", routeError.value);
	} finally {
		busy.value = false;
	}
}
</script>
